import { SeasonalDecor } from '../../seasonal';
import React, { useContext } from 'react';
import { NavLink } from 'react-router-dom';
import AuthContext from '../../auth/AuthContext';
import {
  FiHome,
  FiFileText,
  FiClock,
  FiSettings,
  FiChevronLeft,
  FiChevronRight,
  FiBarChart2,
  FiDollarSign,
  FiShield,
} from 'react-icons/fi';

// Brain icon matching the app logo
const MedInterpretIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    width="28"
    height="28"
  >
    {/* Left hemisphere */}
    <path
      d="M12 3C10 3 8.5 4 7.5 5.5C6.5 4.5 5 4.5 4 5.5C2.5 7 3 9 4 10C3 11 2.5 13 4 14.5C3.5 16 4 17.5 5.5 18.5C6 20 7.5 21 9 21C10 21 11 20.5 12 20"
      stroke="#39E800"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Right hemisphere */}
    <path
      d="M12 3C14 3 15.5 4 16.5 5.5C17.5 4.5 19 4.5 20 5.5C21.5 7 21 9 20 10C21 11 21.5 13 20 14.5C20.5 16 20 17.5 18.5 18.5C18 20 16.5 21 15 21C14 21 13 20.5 12 20"
      stroke="#39E800"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Center line */}
    <path d="M12 3V20" stroke="#39E800" strokeWidth="2" strokeLinecap="round" />
    {/* Neural nodes */}
    <circle cx="7" cy="8" r="1.5" fill="#39E800" />
    <circle cx="17" cy="8" r="1.5" fill="#39E800" />
    <circle cx="6" cy="13" r="1.5" fill="#39E800" />
    <circle cx="18" cy="13" r="1.5" fill="#39E800" />
    <circle cx="8" cy="17" r="1.5" fill="#39E800" />
    <circle cx="16" cy="17" r="1.5" fill="#39E800" />
  </svg>
);

interface SidebarProps {
  isOpen?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen = true,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const navItems = [
    { to: '/', icon: FiHome, label: 'Dashboard' },
    { to: '/interpretations', icon: FiFileText, label: 'Interpretaciones' },
    { to: '/test-data', icon: FiClock, label: 'Datos Pendientes' },
    { to: '/metrics', icon: FiBarChart2, label: 'Metricas' },
    { to: '/costs', icon: FiDollarSign, label: 'Costos' },
    { to: '/config', icon: FiSettings, label: 'Configuracion' },
  ];

  return (
    <aside className={`sidebar ${isOpen ? 'show' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-brand">
        <h1>
          <MedInterpretIcon />
          {!isCollapsed && <span className="brand-text">MedInterpret</span>}
        </h1>
        {!isCollapsed && <span className="brand-subtitle">Panel de Auditoría</span>}
      </div>

      <nav className="sidebar-nav">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'active' : ''}`
            }
            end={item.to === '/'}
            title={isCollapsed ? item.label : undefined}
          >
            <item.icon />
            {!isCollapsed && <span className="link-label">{item.label}</span>}
          </NavLink>
        ))}
        {user?.roles?.includes('reapertura_autorizada') && (
          <NavLink
            to="/admin/totp"
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            title={isCollapsed ? 'Gestión TOTP' : undefined}
          >
            <FiShield />
            {!isCollapsed && <span className="link-label">Gestión TOTP</span>}
          </NavLink>
        )}
      </nav>

      {/* Decoración de temporada: entre el menú y el pie con la flecha. Colapsado, se oculta. */}
      <SeasonalDecor slot="sidebar" hidden={isCollapsed} />

      <div className="sidebar-footer">
        <button
          className="collapse-toggle"
          onClick={onToggleCollapse}
          title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          {isCollapsed ? <FiChevronRight size={18} /> : <FiChevronLeft size={18} />}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
